import { create } from 'zustand';
import { useAsk } from './askStore';
import type { IngredientId } from '../domain/taxonomy';
import { planEnded, type Plan, type PlanEntry } from '../domain/planner';
import type { ListItem } from '../domain/grocery';
import type { Tier } from '../lib/detectContract';
import { todayISO } from '../lib/dates';
import { getRecipe } from '../domain/recipes';
import { afterCooking, type Amount } from '../domain/inventory';
import { durable, load, save, wipe } from './persistence';
import { accountService } from '../lib/account';
import type { CheckIn, CookedEntry, Feedback, PersistedState, Profile, SkipReason } from './schema';
import { unlockRecipe, type Membership } from '../domain/plans';

export interface ReviewItem {
  key: string;
  ingredientId: IngredientId | null;
  rawLabel: string;
  tier: Tier;
  /** kept/removed are user decisions; pending items are never used for recipes. */
  status: 'kept' | 'removed' | 'pending';
  suggestions: IngredientId[];
  source: 'model' | 'user' | 'sample';
  edited: boolean;
}

export interface ReviewSession {
  scanId: string | null;
  source: 'scan' | 'sample' | 'manual';
  items: ReviewItem[];
  startedAt: number;
}

export interface Toast {
  id: number;
  message: string;
  tone: 'info' | 'error';
  action?: { label: string; run: () => void };
}

interface Ephemeral {
  review: ReviewSession | null;
  toast: Toast | null;
  recovered: boolean;
  /** Tonight's pick chosen from the alternatives rail, for this session only. */
  tonightOverride: string | null;
  /** The signed-in account, or null (anonymous, or accounts unavailable). */
  account: { email: string } | null;
  /** True once the saved sign-in has been looked up on this launch (so gates don't flash). */
  accountChecked: boolean;
  /** An action waiting on the person creating an account or signing in. */
  accountPrompt: { reason: string; run: () => void } | null;
  /** Free or Amari Plus. The server decides (snapcook_my_plan); this device caches the answer. */
  membership: Membership;
}

type FeedbackInput = Feedback extends infer F ? (F extends { at: string } ? Omit<F, 'at'> : never) : never;

interface Actions {
  completeOnboarding: (patch: Partial<Profile>, analytics: boolean) => void;
  setConsent: (analytics: boolean) => void;
  updateProfile: (patch: Partial<Profile>) => void;
  setTheme: (t: PersistedState['theme']) => void;
  setCrashReports: (on: boolean) => void;
  /** Answer or dismiss a personalization prompt; either way it is not asked again. */
  resolvePrompt: (id: string, patch: Partial<Profile> | null) => void;
  markPromptShown: () => void;
  dismissMoreMeals: () => void;

  startReview: (session: Omit<ReviewSession, 'startedAt'>) => void;
  updateReviewItem: (key: string, patch: Partial<ReviewItem>) => void;
  addReviewItem: (id: IngredientId) => void;
  commitReview: (mode: 'replace' | 'merge') => IngredientId[];
  cancelReview: () => void;

  addKitchenItem: (id: IngredientId, source?: 'manual' | 'list') => void;
  /** Remove an item; `outcome` tells the learner whether it was used or went unused. */
  removeKitchenItem: (key: string, outcome?: 'used_up' | 'wasted') => void;
  toggleUseSoon: (key: string) => void;
  /** "I checked it, it's fine": restarts the item's freshness estimate from today. */
  markStillFresh: (key: string) => void;
  /** Record how much of an item there is, or null to stop tracking it. */
  setKitchenAmount: (key: string, amount: Amount | null) => void;
  markUsedUp: (ids: IngredientId[]) => void;

  setPlan: (plan: Plan | null) => void;
  /** Move an ended plan into history. Safe to call on every app open. */
  archiveEndedPlan: () => void;
  updatePlanEntry: (date: string, patch: Partial<PlanEntry>) => void;
  skipPlanEntry: (date: string, reason: SkipReason | null) => void;

  setList: (items: ListItem[]) => void;
  /** Add items, skipping any already on the list (unchecking them if they were ticked). */
  addListItems: (items: ListItem[]) => number;
  toggleListItem: (key: string) => void;
  removeListItem: (key: string) => void;
  clearCheckedListItems: () => void;

  toggleSaved: (recipeId: string) => void;
  recordCooked: (recipeId: string, details?: Partial<Pick<CookedEntry, 'rating' | 'notes' | 'took'>>) => void;
  recordFeedback: (f: FeedbackInput) => void;
  setTonightOverride: (recipeId: string | null) => void;
  setInferenceOverride: (key: string, value: 'confirmed' | 'rejected' | null) => void;
  /** Forget everything learned from behaviour, keeping what the user told us. */
  resetLearning: () => void;

  addWeight: (kg: number, date?: string) => void;
  removeWeight: (date: string) => void;
  completeCheckIn: (c: Omit<CheckIn, 'at'>) => void;
  snoozeCheckIn: (until: string) => void;

  showToast: (message: string, opts?: Partial<Omit<Toast, 'id' | 'message'>>) => void;
  dismissToast: () => void;
  wipeAll: () => void;

  setAccount: (account: { email: string } | null) => void;
  setAccountChecked: () => void;
  /** Ask for an account before `run`; runs at once when signed in or when accounts aren't available. */
  requireAccount: (reason: string, run: () => void) => void;
  closeAccountPrompt: () => void;
  setMembership: (m: Membership) => void;
  /** Free plan: open a recipe in full this week. False when the week's allowance is used up. */
  unlockRecipe: (recipeId: string) => boolean;
  /** Replace the durable state (after merging with an account's saved state). */
  replaceDurable: (state: PersistedState) => void;
}

export type AppState = PersistedState & Ephemeral & Actions;

const MEMBERSHIP_KEY = 'amari_membership';
function cachedMembership(): Membership {
  try { return localStorage.getItem(MEMBERSHIP_KEY) === 'plus' ? 'plus' : 'free'; } catch { return 'free'; }
}

const uid = () => crypto.randomUUID();
const nowISO = () => new Date().toISOString();
let toastTimer: ReturnType<typeof setTimeout> | undefined;

const { state: initial, recovered } = load();

export const useStore = create<AppState>()((set, get) => ({
  ...initial,
  review: null,
  toast: null,
  recovered,
  tonightOverride: null,
  account: null,
  accountChecked: false,
  accountPrompt: null,
  membership: cachedMembership(),

  completeOnboarding: (patch, analytics) =>
    set((s) => ({
      onboarded: true,
      onboardedAt: s.onboardedAt ?? nowISO(),
      profile: { ...s.profile, ...patch },
      consent: { analytics, decidedAt: nowISO() },
    })),
  setConsent: (analytics) => set({ consent: { analytics, decidedAt: nowISO() } }),
  updateProfile: (patch) => set((s) => {
    // Hiding or un-hiding a recipe is stamped, so a sync merge keeps the latest choice.
    if (!patch.hiddenRecipes) return { profile: { ...s.profile, ...patch } };
    const before = new Set(s.profile.hiddenRecipes);
    const after = new Set(patch.hiddenRecipes);
    const changed = [...before, ...after].filter((id) => before.has(id) !== after.has(id));
    const at = nowISO();
    return {
      profile: { ...s.profile, ...patch },
      toggledAt: { ...s.toggledAt, ...Object.fromEntries(changed.map((id) => [`hidden:${id}`, at])) },
    };
  }),
  setTheme: (theme) => set({ theme }),
  setCrashReports: (crashReports) => set({ crashReports }),
  resolvePrompt: (id, patch) =>
    set((s) => ({ profile: { ...s.profile, ...(patch ?? {}), prompts: { ...s.profile.prompts, done: { ...s.profile.prompts.done, [id]: nowISO() } } } })),
  markPromptShown: () => set((s) => ({ profile: { ...s.profile, prompts: { ...s.profile.prompts, lastShownAt: nowISO() } } })),
  dismissMoreMeals: () => set({ moreMealsDismissedAt: nowISO() }),

  startReview: (session) => set({ review: { ...session, startedAt: Date.now() } }),
  updateReviewItem: (key, patch) =>
    set((s) => s.review ? { review: { ...s.review, items: s.review.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) } } : {}),
  addReviewItem: (id) =>
    set((s) => {
      if (!s.review) return {};
      const existing = s.review.items.find((i) => i.ingredientId === id);
      if (existing) {
        return { review: { ...s.review, items: s.review.items.map((i) => (i === existing ? { ...i, status: 'kept' } : i)) } };
      }
      const item: ReviewItem = { key: uid(), ingredientId: id, rawLabel: id, tier: 'sure', status: 'kept', suggestions: [], source: 'user', edited: true };
      return { review: { ...s.review, items: [item, ...s.review.items] } };
    }),
  commitReview: (mode) => {
    const { review, kitchen } = get();
    if (!review) return [];
    const now = nowISO();
    const kept = [...new Set(review.items.filter((i) => i.status === 'kept' && i.ingredientId).map((i) => i.ingredientId!))];
    const prev = mode === 'merge' ? kitchen.items : kitchen.items.filter((i) => i.source === 'manual');
    const byId = new Map(prev.map((i) => [i.ingredientId, i]));
    // Food that was already here keeps its added date (and use-soon flag), so a re-scan doesn't
    // reset its freshness estimate.
    const before = new Map(kitchen.items.map((i) => [i.ingredientId, i]));
    for (const id of kept) {
      const old = before.get(id);
      if (!byId.has(id)) byId.set(id, { key: uid(), ingredientId: id, useSoon: old?.useSoon ?? false, addedAt: old?.addedAt ?? now, source: review.source === 'sample' ? 'sample' : 'scan', amount: null });
    }
    set({ kitchen: { items: [...byId.values()], updatedAt: now }, review: null });
    return kept;
  },
  cancelReview: () => set({ review: null }),

  addKitchenItem: (id, source = 'manual') =>
    set((s) => s.kitchen.items.some((i) => i.ingredientId === id)
      ? {}
      : { kitchen: { items: [{ key: uid(), ingredientId: id, useSoon: false, addedAt: nowISO(), source, amount: null }, ...s.kitchen.items], updatedAt: nowISO() } }),
  removeKitchenItem: (key, outcome) =>
    set((s) => {
      const item = s.kitchen.items.find((i) => i.key === key);
      const feedback = item && outcome ? [{ type: outcome, ingredientId: item.ingredientId, at: nowISO() } as Feedback, ...s.feedback].slice(0, 500) : s.feedback;
      return { kitchen: { items: s.kitchen.items.filter((i) => i.key !== key), updatedAt: nowISO() }, feedback };
    }),
  markStillFresh: (key) =>
    set((s) => ({ kitchen: { ...s.kitchen, items: s.kitchen.items.map((i) => (i.key === key ? { ...i, addedAt: nowISO(), useSoon: false } : i)) } })),
  toggleUseSoon: (key) =>
    set((s) => ({ kitchen: { ...s.kitchen, items: s.kitchen.items.map((i) => (i.key === key ? { ...i, useSoon: !i.useSoon } : i)) } })),
  setKitchenAmount: (key, amount) =>
    set((s) => ({ kitchen: { items: s.kitchen.items.map((i) => (i.key === key ? { ...i, amount } : i)), updatedAt: nowISO() } })),
  markUsedUp: (ids) =>
    set((s) => ({
      kitchen: { items: s.kitchen.items.filter((i) => !ids.includes(i.ingredientId)), updatedAt: nowISO() },
      feedback: [...ids.map((ingredientId) => ({ type: 'used_up' as const, ingredientId, at: nowISO() })), ...s.feedback].slice(0, 500),
    })),

  setPlan: (plan) => set({ plan }),
  archiveEndedPlan: () =>
    set((s) => {
      if (!s.plan || !planEnded(s.plan, todayISO())) return {};
      return { plan: null, planHistory: [s.plan, ...s.planHistory].slice(0, 12) };
    }),
  updatePlanEntry: (date, patch) =>
    set((s) => s.plan ? { plan: { ...s.plan, entries: s.plan.entries.map((e) => (e.date === date ? { ...e, ...patch } : e)) } } : {}),
  skipPlanEntry: (date, reason) =>
    set((s) => {
      if (!s.plan) return {};
      const entry = s.plan.entries.find((e) => e.date === date);
      if (!entry) return {};
      return {
        plan: { ...s.plan, entries: s.plan.entries.map((e) => (e.date === date ? { ...e, status: 'skipped' as const, skipReason: reason } : e)) },
        feedback: [{ type: 'skip' as const, recipeId: entry.recipeId, reason, at: nowISO() }, ...s.feedback].slice(0, 500),
      };
    }),

  setList: (list) => set({ list }),
  addListItems: (items) => {
    const existing = new Map(get().list.map((i) => [i.key, i]));
    let added = 0;
    for (const item of items) {
      const prev = existing.get(item.key);
      if (!prev) { existing.set(item.key, item); added++; }
      else if (prev.checked) existing.set(item.key, { ...prev, checked: false });
    }
    set({ list: [...existing.values()] });
    return added;
  },
  toggleListItem: (key) => set((s) => ({ list: s.list.map((i) => (i.key === key ? { ...i, checked: !i.checked } : i)) })),
  removeListItem: (key) => set((s) => ({ list: s.list.filter((i) => i.key !== key) })),
  clearCheckedListItems: () => set((s) => ({ list: s.list.filter((i) => !i.checked) })),

  toggleSaved: (recipeId) =>
    set((s) => ({
      saved: s.saved.includes(recipeId) ? s.saved.filter((x) => x !== recipeId) : [recipeId, ...s.saved],
      toggledAt: { ...s.toggledAt, [`saved:${recipeId}`]: nowISO() },
    })),
  recordCooked: (recipeId, details = {}) =>
    set((s) => {
      const today = todayISO();
      let fromPlan = false;
      let servings = s.profile.household;
      const plan = s.plan
        ? {
          ...s.plan,
          entries: s.plan.entries.map((e) => {
            if (!fromPlan && e.recipeId === recipeId && e.status === 'planned' && e.date <= today) {
              fromPlan = true;
              servings = e.servings;
              return { ...e, status: 'cooked' as const };
            }
            return e;
          }),
        }
        : null;
      const entry: CookedEntry = { recipeId, at: nowISO(), rating: details.rating ?? null, notes: details.notes ?? [], took: details.took ?? null, fromPlan };
      // Draw down the amounts the user tracks; untracked items are left alone.
      const recipe = getRecipe(recipeId);
      const left = recipe ? afterCooking(s.kitchen.items, recipe, servings) : new Map();
      const items = s.kitchen.items.map((i) => (i.amount && left.has(i.ingredientId) ? { ...i, amount: left.get(i.ingredientId)! } : i));
      return { cooked: [entry, ...s.cooked].slice(0, 300), plan, tonightOverride: null, kitchen: { ...s.kitchen, items } };
    }),
  recordFeedback: (f) => set((s) => ({ feedback: [{ ...f, at: nowISO() } as Feedback, ...s.feedback].slice(0, 500) })),
  setTonightOverride: (tonightOverride) => set({ tonightOverride }),
  setInferenceOverride: (key, value) =>
    set((s) => {
      const next = { ...s.inferenceOverrides };
      if (value) next[key] = value; else delete next[key];
      return { inferenceOverrides: next };
    }),
  resetLearning: () => set({ feedback: [], inferenceOverrides: {}, cooked: get().cooked.map((c) => ({ ...c, rating: null, notes: [], took: null })) }),

  addWeight: (kg, date = todayISO()) =>
    set((s) => ({ weights: [...s.weights.filter((w) => w.date !== date), { date, kg: Math.round(kg * 10) / 10 }].sort((a, b) => a.date.localeCompare(b.date)) })),
  removeWeight: (date) => set((s) => ({ weights: s.weights.filter((w) => w.date !== date) })),
  completeCheckIn: (c) => set((s) => ({ checkIns: [{ ...c, at: nowISO() }, ...s.checkIns].slice(0, 40), checkInSnoozedUntil: null })),
  snoozeCheckIn: (until) => set({ checkInSnoozedUntil: until }),

  showToast: (message, opts) => {
    clearTimeout(toastTimer);
    const toast: Toast = { id: Date.now(), message, tone: opts?.tone ?? 'info', action: opts?.action };
    set({ toast });
    toastTimer = setTimeout(() => get().dismissToast(), toast.tone === 'error' || toast.action ? 7000 : 4000);
  },
  dismissToast: () => set({ toast: null }),

  wipeAll: () => {
    wipe();
    try { localStorage.removeItem(MEMBERSHIP_KEY); } catch { /* private mode */ }
    useAsk.getState().clear();
    set({ ...load().state, review: null, toast: null, recovered: false, tonightOverride: null, account: null, accountPrompt: null, membership: 'free' });
  },

  setAccount: (account) => set({ account }),
  setAccountChecked: () => set({ accountChecked: true }),
  requireAccount: (reason, run) => {
    if (get().account || !accountService()) { run(); return; }
    set({ accountPrompt: { reason, run } });
  },
  closeAccountPrompt: () => set({ accountPrompt: null }),
  setMembership: (membership) => {
    try { localStorage.setItem(MEMBERSHIP_KEY, membership); } catch { /* private mode */ }
    set({ membership });
  },
  unlockRecipe: (recipeId) => {
    if (get().membership === 'plus') return true;
    const next = unlockRecipe(get().recipeUnlocks, recipeId, todayISO());
    if (!next) return false;
    set({ recipeUnlocks: next });
    return true;
  },
  replaceDurable: (state) => set({ ...state }),
}));

// Persist only the durable slice, debounced.
let saveTimer: ReturnType<typeof setTimeout> | undefined;
useStore.subscribe((s) => {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => save(durable(s)), 150);
});
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => save(durable(useStore.getState())));
}
